(() => {
  'use strict';

  const gap = '\u202f';
  const spaces = '[\\t\\n\\r \\u00a0\\u202f]*';
  const units = ['km/h', 'm/s', 'kg/m³', 'g/m²', 'N·m', 'N/m', 'U/min', 'kWh', 'mAh', 'kPa', 'MPa', 'MHz', 'kHz', 'kW', 'Wh', 'Ah', 'mV', 'mA', 'Nm', 'Hz', 'Pa', 'mm²', 'cm²', 'km²', 'mm³', 'cm³', 'mm', 'cm', 'km', 'mg', 'µg', 'kg', 'ml', 'mL', 'cl', 'dl', '°C', '°F', 'bar', 'PSI', 'psi', 'lm', 'lx', 'ms', 'min', 'rpm', 'TPI', 'tpi', 'lbs', 'lb', 'oz', 'ft', 'dB', 'EUR', 'CHF', 'USD', 'GBP', 'Zoll', 'Stück', 'Liter', 'Millimeter', 'Zentimeter', 'Meter', 'Kilometer', 'Gramm', 'Kilogramm', 'Volt', 'Watt', 'g', 'm', 'l', 'L', 'W', 'V', 'A', 'N', 'h', 's', 'm²', 'm³', '%', '€', '$', '£'];
  const escapedUnits = units.sort((a, b) => b.length - a.length).map(unit => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const unitEnd = '(?![\\p{L}\\p{N}_])';
  const number = '(?:\\d{1,3}(?:[ \\u00a0\\u202f]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)*(?:/\\d+)?)';
  const measurement = new RegExp('(?<![\\p{L}\\p{N}_])(' + number + ')' + spaces + '(' + escapedUnits + ')' + unitEnd, 'gu');
  const currency = new RegExp('([€$£])' + spaces + '(?=[+−-]?\\d)', 'gu');
  const leadingUnit = new RegExp('^' + spaces + '(' + escapedUnits + ')' + unitEnd, 'u');
  const excluded = 'script,style,noscript,template,textarea,input,pre,code,kbd,samp,svg,math,iframe,.product__sku,.product__barcode,[data-update-block="sku-barcode"],[contenteditable]:not([contenteditable="false"]),[data-fs-units="off"]';
  const inlineTags = new Set(['SPAN', 'STRONG', 'B', 'EM', 'I', 'SMALL', 'A', 'SUP', 'SUB', 'MARK', 'S', 'DEL', 'INS', 'U']);
  const seen = new WeakMap();
  const optionLabels = new WeakMap();
  const attributeValues = new WeakMap();
  const pending = new Set();
  let scheduled = false;

  function format(text) {
    if (/https?:\/\/|www\.|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/u.test(text)) return text;
    return text
      .replace(/„([^„“”"]*?)[”"]/gu, '„$1“')
      .replace(/“([^“”„"]*?)[”"]/gu, '„$1“')
      .replace(/(^|[\s([{>:])"(?=\S)([^"\r\n]+?)"(?=$|[\s.,;:!?)}\]])/gu, '$1„$2“')
      .replace(measurement, (_, amount, unit) => amount + gap + unit)
      .replace(currency, '$1' + gap);
  }

  function previousText(node) {
    let cursor = node;
    while (cursor) {
      if (cursor.previousSibling) {
        cursor = cursor.previousSibling;
        while (cursor.nodeType === 1 && inlineTags.has(cursor.tagName) && !cursor.matches(excluded) && cursor.lastChild) cursor = cursor.lastChild;
        return cursor.nodeType === 3 ? cursor : null;
      }
      cursor = cursor.parentNode;
      if (!cursor || cursor.nodeType !== 1 || !inlineTags.has(cursor.tagName)) return null;
    }
    return null;
  }

  function write(node, value) {
    seen.set(node, value);
    if (node.data !== value) node.data = value;
  }

  function formatNode(node) {
    if (!node.parentElement || node.parentElement.closest(excluded + ',option')) return;
    if (seen.get(node) === node.data && !leadingUnit.test(node.data)) return;
    let text = format(node.data);
    if (leadingUnit.test(text)) {
      let previous = previousText(node);
      const blanks = [];
      while (previous && /^[\t\n\r \u00a0\u202f]*$/u.test(previous.data) && blanks.length < 8) {
        blanks.push(previous);
        previous = previousText(previous);
      }
      if (previous && /\d[\t\n\r \u00a0\u202f]*$/u.test(previous.data)) {
        write(previous, previous.data.replace(/[\t\n\r \u00a0\u202f]+$/u, ''));
        blanks.forEach(blank => write(blank, ''));
        text = text.replace(leadingUnit, (_, unit) => gap + unit);
      }
    }
    const preceding = previousText(node);
    if (/^[\t\n\r \u00a0\u202f]*[+−-]?\d/u.test(text) && preceding && /[€$£][\t\n\r \u00a0\u202f]*$/u.test(preceding.data)) {
      write(preceding, preceding.data.replace(/[\t\n\r \u00a0\u202f]*$/u, gap));
      text = text.replace(/^[\t\n\r \u00a0\u202f]+/u, '');
    }
    write(node, text);
  }

  function formatOption(option) {
    if (option.closest(excluded)) return;
    const previous = optionLabels.get(option);
    const explicitLabel = option.getAttribute('label');
    const fromText = explicitLabel === null || (previous?.fromText && explicitLabel === previous.written);
    const label = fromText ? option.textContent : explicitLabel;
    const formatted = format(label);
    optionLabels.set(option, { written: formatted, fromText });
    if (formatted !== explicitLabel && (formatted !== label || explicitLabel !== null)) option.setAttribute('label', formatted);
  }

  function formatAttributes(element) {
    if (element.closest(excluded)) return;
    const values = {};
    for (const name of ['title', 'aria-label']) {
      const value = element.getAttribute(name);
      if (value === null) continue;
      values[name] = format(value);
      if (values[name] !== value) element.setAttribute(name, values[name]);
    }
    attributeValues.set(element, values);
  }

  function process(root) {
    if (!root.isConnected) return;
    if (root.nodeType === 3) {
      formatNode(root);
      return;
    }
    if (root.nodeType !== 1 || root.closest(excluded)) return;
    formatAttributes(root);
    root.querySelectorAll('[title],[aria-label]').forEach(formatAttributes);
    if (root.tagName === 'OPTION') formatOption(root);
    root.querySelectorAll('option').forEach(formatOption);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) formatNode(node);
  }

  function queue(root) {
    if (!root) return;
    pending.add(root);
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const roots = Array.from(pending);
      pending.clear();
      roots.filter(root => !roots.some(other => other !== root && other.contains(root))).forEach(process);
    });
  }

  function start() {
    process(document.body);
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'characterData') {
          if (seen.get(record.target) !== record.target.data) queue(record.target.parentElement);
        } else if (record.type === 'attributes') {
          if (record.target.tagName === 'OPTION' && optionLabels.get(record.target)?.written !== record.target.getAttribute('label')) queue(record.target);
          else if (record.attributeName !== 'label' && attributeValues.get(record.target)?.[record.attributeName] !== record.target.getAttribute(record.attributeName)) queue(record.target);
        } else {
          if (record.addedNodes.length || record.target.tagName === 'OPTION') queue(record.target);
        }
      }
    }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['label', 'title', 'aria-label'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
